/**
 * Elastic APM RUM agent (web-client-rum-and-replay, 2026-10-05, plan "C + Mixpanel" part 2).
 *
 * `init()` is a no-op unless `rum.serverUrl` is set in the runtime config (see ../config.ts) --
 * empty/absent means local dev (and any environment without an intake) keeps working with zero
 * agent overhead, same fail-open shape every other runtime-config
 * key here already uses.
 *
 * `captureError`/`addLabels` are safe to call even when the agent was never initialized (they
 * no-op against `apmBase`'s own guards when `active` is false) -- callers (route-error.tsx,
 * providers.tsx) never need to check whether RUM is on.
 *
 * Production runs in strict mode (`isStrict`): same agent, but nothing that identifies a person
 * leaves the browser, and one page in ten is reported. Tests: `rum-strict.test.ts`.
 *
 * What a trace covers, and how screens end it: `page-trace.ts`, `route-tracing.tsx`,
 * `lib/use-page-ready.ts`.
 */
import { init as initApmRum, apm } from "@elastic/apm-rum";

import { runtimeConfig } from "../config";

import { holdPageLoad, markRum } from "./page-trace";

let initialized = false;
/** Strict mode (production, see `isStrict`): set by `initRum`, read by `setRumUser`. */
let strictMode = false;

/** The share of page loads and navigations a production browser reports. */
export const STRICT_SAMPLE_RATE = 0.1;

/** Strict mode keeps production's browser traces free of personal data: customer data stays
 * out of telemetry whatever the intake. On when the environment is `prod`, or when the runtime config says `rum.strict`.
 * Nothing turns it off in `prod`. */
export function isStrict(config: { environment: string; rum: { strict?: boolean } }): boolean {
  return config.environment === "prod" || config.rum.strict === true;
}

/** Cuts the query string and the fragment. */
function stripQuery(url: string): string {
  try {
    const u = new URL(url);
    u.search = "";
    u.hash = "";
    return u.toString();
  } catch {
    // A relative address: cut at the "#" and the "?" by hand.
    return url.split("#")[0]!.split("?")[0] ?? url;
  }
}

/** Cuts the query and fragment, and puts `:id` in every id-like path segment of an address. */
function stripUrl(url: string): string {
  const bare = stripQuery(url);
  try {
    const u = new URL(bare);
    u.pathname = scrubName(u.pathname);
    return u.toString();
  } catch {
    return scrubName(bare);
  }
}

/** A path segment that is an id or an address, not a word of the route: a UUID, a ULID, a number, a mix
 * of letters and digits of 6 or more, or anything holding "@". */
const ID_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-.*|[0-9a-hjkmnp-tv-z]{26}|\d+|(?=.*\d)[\w-]{6,}|.*(?:@|%40).*)$/i;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** `GET /api/v1/candidates/3f2a...` -> `GET /api/v1/candidates/:id`, so a transaction the agent
 * names after a request carries no id. */
export function scrubName(name: string): string {
  return name
    .split("#")[0]!
    .split("?")[0]!
    .split("/")
    .map((segment) => (ID_SEGMENT.test(safeDecode(segment)) ? ":id" : segment))
    .join("/");
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Loose = { [key: string]: any };

/** The labels strict mode lets through: fixed words the app itself sets, never a value from the
 * user or the data. */
const STRICT_LABELS: Record<string, true> = { kind: true, status: true, page_ready: true };

type Scrubbable = {
  name?: string;
  type?: string;
  culprit?: string;
  context?: Loose;
  exception?: Loose;
  log?: Loose;
  spans?: Scrubbable[];
};

/** Runs on every outgoing batch. Query strings and fragments can carry PII or tokens (an OAuth
 * callback's `code`, a search for a person's name): they are cut from the page, request and every
 * request span URL, and every name and URL carries `:id` for an id-like path segment, in every
 * environment (a request span is named after the address it fetched). The context types are loose (`{ [key: string]: any }`), so each field is read
 * defensively.
 *
 * `strict` (production) adds: the user context is gone,
 * only the labels in `STRICT_LABELS` stay, and an error keeps its type and stack but not its
 * message (which may repeat what a user typed). */
export function scrubPayload<T extends { transactions?: unknown[]; errors?: unknown[] }>(
  payload: T,
  strict = false,
): T {
  const scrubContext = (context: Loose | undefined) => {
    if (!context) return;
    for (const key of ["page", "request", "http"]) {
      const part = context[key] as { url?: string; referer?: string } | undefined;
      if (part?.url) part.url = stripUrl(part.url);
      if (part?.referer) part.referer = stripUrl(part.referer);
    }
    // The agent's click context (`custom.classes`, an element's class list) and nothing else of
    // ours lives here: gone in every environment.
    delete context.custom;
    if (!strict) return;
    delete context.user;
    delete context.response;
    if (context.request) delete context.request.headers;
    for (const bag of ["tags", "labels"]) {
      const labels = context[bag] as Loose | undefined;
      if (!labels) continue;
      for (const key of Object.keys(labels)) {
        if (!STRICT_LABELS[key]) delete labels[key];
        else if (typeof labels[key] === "string") labels[key] = scrubName(labels[key]);
      }
    }
  };
  const scrubTransaction = (item: Scrubbable) => {
    scrubContext(item.context);
    // Every name: a request span or transaction is `GET /api/v1/candidates/<id>`.
    if (item.name) item.name = scrubName(item.name);
    for (const span of item.spans ?? []) scrubTransaction(span);
  };
  for (const transaction of (payload.transactions ?? []) as Scrubbable[]) {
    scrubTransaction(transaction);
  }
  for (const error of (payload.errors ?? []) as Scrubbable[]) {
    scrubContext(error.context);
    if (!strict) continue;
    if (error.exception) {
      error.exception.message = error.exception.type ?? "Error";
      for (const frame of (error.exception.stacktrace ?? []) as Array<{
        abs_path?: string;
        filename?: string;
      }>) {
        if (frame.abs_path) frame.abs_path = stripQuery(frame.abs_path);
        if (frame.filename) frame.filename = stripQuery(frame.filename);
      }
    }
    if (error.log) error.log.message = "(redacted)";
    if (error.culprit) error.culprit = stripQuery(error.culprit);
  }
  return payload;
}

/** Call once, after `loadConfig()` resolves (see main.tsx) -- the server URL/service version
 * only exist once the runtime config has loaded. */
export function initRum(): void {
  if (initialized) return;
  const config = runtimeConfig();
  const { rum, environment } = config;
  if (!rum.serverUrl) return; // RUM off (local dev, or an environment with no intake).
  initialized = true;
  const strict = isStrict(config);
  strictMode = strict;

  initApmRum({
    serviceName: "web",
    serverUrl: rum.serverUrl,
    serviceVersion: rum.serviceVersion || "unknown",
    environment,
    // web-api is same-origin (nginx proxies /api -- web/helm/templates/ingress.yaml), so the
    // browser's own origin is the one distributed-tracing header target: every /api fetch gets
    // a traceparent header, linking the RUM transaction to web-api's own trace (confirmed no
    // CORS layer exists on web-api at all -- same-origin requests need none).
    distributedTracingOrigins: [window.location.origin],
    // Production reports one page in ten; every other environment reports all.
    transactionSampleRate: strict ? STRICT_SAMPLE_RATE : 1.0,
    // The agent's history hook names a route-change transaction after `document.title`, which
    // can hold a person's name. `RouteTracing` starts them, named by the route pattern.
    // Its `click` instrumentation starts a transaction on a click and puts the element's class
    // list in `context.custom`; `RouteTracing` does the job and the filter drops `custom`.
    disableInstrumentations: ["history", "click"],
    // The name until the first screen renames it (sign-in, config, startup): never the address.
    pageLoadTransactionName: "/(app start)",
  });
  apm.addFilter((payload) => scrubPayload(payload, strict));
  markRum("config_loaded");
  holdPageLoad();
}

/** Internal user id ONLY -- never email/name (David's standing rule: no PII in telemetry). Call
 * after sign-in; call with `null` on sign-out to clear it. */
export function setRumUser(internalUserId: string | null): void {
  // Strict mode sends no user at all, not even the internal id (the filter drops it again).
  if (!initialized || strictMode) return;
  apm.setUserContext(internalUserId ? { id: internalUserId } : {});
}

/** Report an error RUM never saw on its own (React's error boundary / router errorElement path
 * -- the agent's default window.onerror/unhandledrejection hooks only catch what reaches the
 * window, not what React swallows inside its own render loop). `labels` go on as string tags
 * (APM RUM labels are flat key/value, no nesting) -- e.g. { endpoint, status } for a failed
 * query -- scoped to THIS error via captureError's own `opts.labels` (NOT apm.addLabels(), which
 * mutates the agent's shared/global label set and would leak onto every later error and
 * transaction until something else happens to overwrite the same keys -- found by this PR's own
 * local review, 2026-10-05). No-ops quietly if RUM was never initialized. */
export function captureError(error: unknown, labels?: Record<string, string | number>): void {
  if (!initialized) return;
  const err = error instanceof Error ? error : new Error(String(error));
  apm.captureError(err, labels ? { labels } : undefined);
}
